import React, { useState, useRef, useEffect } from 'react';
import './App.css';
import Deck from './cards';
import OnboardingOverlay from './components/OnboardingOverlay';
import ConfirmDialog from './components/ConfirmDialog';

function Card({ color, number, display, isFlipped, image, onClick }) {
  const handlePointerDown = (e) => {
    // Capture pointer events
    e.target.setPointerCapture(e.pointerId);
    if (onClick) onClick(e);
  };

  return (
    <div 
      className={`card ${isFlipped ? 'flipped' : ''}`} 
      onPointerDown={handlePointerDown}
    >
      <div className='card-front'>
        <img src={image} alt={display} style={{ width: '100%', height: '100%' }} />
      </div>
      <div className='card-back'/>
    </div>
  );
}

function App() {
  const [deckInstance] = useState(() => new Deck());
  const [drawPile, setDrawPile] = useState([]);
  const [remainingCards, setRemainingCards] = useState(deckInstance.getRemainingCards());
  const [isLoading, setIsLoading] = useState(false);
  const previousCardsRef = useRef(null);
  const [maxPreviousCards, setMaxPreviousCards] = useState(5);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showConfirmShuffle, setShowConfirmShuffle] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1200) {
        setMaxPreviousCards(15);
      } else if (window.innerWidth >= 768) {
        setMaxPreviousCards(10);
      } else {
        setMaxPreviousCards(5);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  async function handleDraw() {
    if (isLoading) return;
    setIsLoading(true);
    
    const drawnCard = deckInstance.draw();
    if (drawnCard) {
      setDrawPile(currentDrawPile => {
        return [drawnCard, ...currentDrawPile];
      });
      setRemainingCards(deckInstance.getRemainingCards());
    }
    
    await new Promise(resolve => setTimeout(resolve, 300));
    setIsLoading(false);
  }

  function handleShuffle() {
    setShowConfirmShuffle(true);
  }

  function confirmShuffle() {
    deckInstance.shuffle();
    setDrawPile([]);
    setRemainingCards(deckInstance.getRemainingCards());
    setShowConfirmShuffle(false);
  }

  const handleScroll = (direction) => {
    if (previousCardsRef.current) {
      const scrollAmount = 150; // Adjust based on your card width
      previousCardsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="App">
      <div className="header-container">
        <div className="header-top">
          <h1>Sweet Draw</h1>
          <button 
            className="help-button" 
            onClick={() => setShowOverlay(true)}
            aria-label="Show instructions"
          >
            ?
          </button>
        </div>
        <h2>Remaining Cards: {remainingCards}</h2>
        <div className="button-container">
          <button 
            onClick={handleDraw} 
            className="draw-button" 
            disabled={remainingCards === 0 || isLoading}>
            {isLoading ? 'Drawing...' : 'Draw'}
          </button>
          <button 
            onClick={handleShuffle} 
            className="shuffle-button">
            Shuffle
          </button>
        </div>
      </div>

      <main>
        {drawPile.length > 0 && (
          <div className={`current-card ${isLoading ? 'loading' : ''}`}>
            <h2>Current Card</h2>
            <Card
              color={drawPile[0].color}
              number={drawPile[0].number}
              display={drawPile[0].display}
              isFlipped={true}
              image={drawPile[0].image}
            />
          </div>
        )}

        {drawPile.length > 1 && (
          <div className="previous-cards-container">
            <div className="previous-cards-header">
              <h2>Previous Cards</h2>
              <div className="scroll-arrows">
                <span className="scroll-arrow" onClick={() => handleScroll('left')}>←</span>
                <span className="scroll-arrow" onClick={() => handleScroll('right')}>→</span>
              </div>
            </div>
            <div className="previous-cards" ref={previousCardsRef}>
              {drawPile.slice(1, maxPreviousCards + 1).map((card, index) => (
                <Card
                  key={index}
                  color={card.color}
                  number={card.number}
                  display={card.display}
                  isFlipped={true}
                  image={card.image}
                />
              ))}
            </div>
          </div>
        )}

        {drawPile.length === 0 && (
          <div className="empty-state">
            <h3>Ready to Play!</h3>
            <p>Tap Draw to start your turn</p>
          </div>
        )}
      </main>
      <footer className="footer">
        <p>Not affiliated with Candyland. All rights reserved.</p>
        <p>Follow us on{' '}
          <a 
            href="https://x.com/chihware?ref=SweetDraw" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            X @chihware
          </a>
        </p>
      </footer>
      {showOverlay && (
        <OnboardingOverlay onClose={() => setShowOverlay(false)} />
      )}
      {showConfirmShuffle && (
        <ConfirmDialog 
          onConfirm={confirmShuffle}
          onCancel={() => setShowConfirmShuffle(false)}
        />
      )}
    </div>
  );
}

export default App;